/**
 * Utilitários para tratar `birthDate` como dia civil em UTC.
 *
 * Por que UTC? Data de nascimento é um dia civil — "18/07/1957" é o mesmo
 * dia em qualquer fuso. Misturar timezone local com epoch ms causa o bug
 * clássico: `new Date("1957-07-18")` é UTC midnight, e em UTC-3 vira
 * "17/07/1957 21:00" local — exibido 1 dia antes em `toLocaleDateString`
 * ou `getDate()/getMonth()`.
 *
 * Convenção: storage sempre como `Date.UTC(y, m-1, d)`, leitura sempre
 * via `getUTC*`. Idade compara componentes UTC do nascimento com data
 * local de hoje (idade vira na "data de hoje" do usuário).
 */

/**
 * `true` quando há data de nascimento. NUNCA testar `birthDate` por
 * truthiness: 01/01/1970 é `Date.UTC(1970, 0, 1) === 0` — a data some da
 * tela e o paciente parece "sem nascimento" mesmo salvo certo.
 */
export function hasBirthDate(epoch: unknown): epoch is number {
  return typeof epoch === 'number' && Number.isFinite(epoch);
}

/** Parse "YYYY-MM-DD" (input[type=date] ou ISO date-only) → UTC midnight epoch. */
export function parseBirthDateInput(value: string | undefined | null): number | undefined {
  if (!value) return undefined;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return undefined;
  const y = Number(m[1]);
  const mm = Number(m[2]);
  const dd = Number(m[3]);
  if (!y || !mm || !dd) return undefined;
  return Date.UTC(y, mm - 1, dd);
}

/** Epoch (UTC midnight) → "YYYY-MM-DD" pra popular <input type="date">. */
export function birthDateToInputValue(epoch: number | undefined | null): string {
  if (!hasBirthDate(epoch)) return '';
  const d = new Date(epoch);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Epoch (UTC midnight) → "DD/MM/YYYY" pra exibição BR. */
export function formatBirthDateBR(epoch: number | undefined | null): string {
  if (!hasBirthDate(epoch)) return '';
  const d = new Date(epoch);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/** Idade em anos: usa data UTC do nascimento e data local de hoje. */
export function calculateAgeFromBirthDate(epoch: number | undefined | null): number | undefined {
  if (!hasBirthDate(epoch)) return undefined;
  const d = new Date(epoch);
  if (Number.isNaN(d.getTime())) return undefined;
  const today = new Date();
  const birthY = d.getUTCFullYear();
  const birthM = d.getUTCMonth();
  const birthD = d.getUTCDate();
  let age = today.getFullYear() - birthY;
  if (
    today.getMonth() < birthM ||
    (today.getMonth() === birthM && today.getDate() < birthD)
  ) {
    age--;
  }
  return age >= 0 && age < 130 ? age : undefined;
}

/** Idade decomposta em anos, meses e dias. */
export interface AgeParts {
  years: number;
  months: number;
  days: number;
}

/**
 * Idade detalhada (anos, meses e dias): compara os componentes civis do
 * nascimento (UTC) com a data local de hoje, "emprestando" dias do mês
 * anterior quando o dia de hoje é menor que o do nascimento. Mesma convenção
 * de `calculateAgeFromBirthDate`. Retorna `undefined` pra data ausente,
 * inválida, futura ou ≥ 130 anos.
 */
export function calculateAgePartsFromBirthDate(
  epoch: number | undefined | null,
): AgeParts | undefined {
  if (!hasBirthDate(epoch)) return undefined;
  const d = new Date(epoch);
  if (Number.isNaN(d.getTime())) return undefined;
  const today = new Date();
  let years = today.getFullYear() - d.getUTCFullYear();
  let months = today.getMonth() - d.getUTCMonth();
  let days = today.getDate() - d.getUTCDate();
  if (days < 0) {
    months -= 1;
    // Dias do mês anterior ao de hoje (dia 0 do mês atual = último do anterior).
    days += new Date(today.getFullYear(), today.getMonth(), 0).getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  return years >= 0 && years < 130 ? { years, months, days } : undefined;
}

/**
 * Idade detalhada formatada pra exibição: "44a 9m 12d". Omite meses/dias
 * quando zero, mas sempre mostra os anos (ex.: recém-nascido → "0a 0m 3d").
 * String vazia quando não há data válida.
 */
export function formatAgeDetailed(epoch: number | undefined | null): string {
  const p = calculateAgePartsFromBirthDate(epoch);
  if (!p) return '';
  const parts = [`${p.years}a`];
  if (p.months > 0) parts.push(`${p.months}m`);
  if (p.days > 0) parts.push(`${p.days}d`);
  return parts.join(' ');
}

/**
 * Idade CURTA pro cabeçalho recolhido: só os anos ("45a"). Criança pequena
 * mantém os meses — abaixo de 3 anos "2a 4m", abaixo de 1 ano "8m 12d".
 * String vazia quando não há data válida.
 */
export function formatAgeShort(epoch: number | undefined | null): string {
  const p = calculateAgePartsFromBirthDate(epoch);
  if (!p) return '';
  if (p.years >= 3) return `${p.years}a`;
  if (p.years >= 1) return p.months > 0 ? `${p.years}a ${p.months}m` : `${p.years}a`;
  return p.months > 0 ? `${p.months}m ${p.days}d` : `${p.days}d`;
}

/** Verifica se o aniversário do paciente é hoje (data local). */
export function isBirthdayTodayFromBirthDate(epoch: number | undefined | null): boolean {
  if (!hasBirthDate(epoch)) return false;
  const d = new Date(epoch);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  return d.getUTCDate() === today.getDate() && d.getUTCMonth() === today.getMonth();
}

/** Proximidade do aniversário em relação a hoje. */
export interface BirthdayProximity {
  /** Está dentro da janela (±`windowDays`) ao redor do aniversário. */
  inWindow: boolean;
  /** O aniversário é exatamente hoje. */
  isToday: boolean;
  /**
   * Dias até o aniversário mais próximo: 0 = hoje, positivo = ainda vai
   * chegar (ex.: +2 = daqui a 2 dias), negativo = já passou (ex.: -1 = ontem).
   * `NaN` quando não há data válida.
   */
  dayOffset: number;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Calcula a proximidade do aniversário em relação a HOJE (data local), dentro
 * de uma janela de ±`windowDays` dias (padrão 2 → janela de 5 dias).
 *
 * Compara o dia/mês civis do nascimento (componentes UTC do epoch, mesma
 * convenção do resto do módulo) com a data local de hoje. Avalia a ocorrência
 * do aniversário no ano passado, neste ano e no próximo, e escolhe a mais
 * próxima — assim a virada de ano funciona (nasceu 31/12 e hoje é 02/01 conta
 * como "há 2 dias"). 29/02 em ano não-bissexto cai naturalmente em 01/03.
 */
export function getBirthdayProximity(
  epoch: number | undefined | null,
  windowDays = 2,
): BirthdayProximity {
  const none: BirthdayProximity = { inWindow: false, isToday: false, dayOffset: NaN };
  if (!hasBirthDate(epoch)) return none;
  const d = new Date(epoch);
  if (Number.isNaN(d.getTime())) return none;

  const birthM = d.getUTCMonth();
  const birthD = d.getUTCDate();
  const now = new Date();
  const todayMid = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

  let best = none;
  for (const yearShift of [-1, 0, 1]) {
    const occ = new Date(now.getFullYear() + yearShift, birthM, birthD).getTime();
    const offset = Math.round((occ - todayMid) / MS_PER_DAY);
    if (Number.isNaN(best.dayOffset) || Math.abs(offset) < Math.abs(best.dayOffset)) {
      best = {
        inWindow: Math.abs(offset) <= windowDays,
        isToday: offset === 0,
        dayOffset: offset,
      };
    }
  }
  return best;
}

/** Idade que a pessoa faz num ano específico (usado em BirthdaysReport). */
export function ageInYearFromBirthDate(
  epoch: number | undefined | null,
  targetYear: number,
): number | null {
  if (!hasBirthDate(epoch)) return null;
  const d = new Date(epoch);
  if (Number.isNaN(d.getTime())) return null;
  return targetYear - d.getUTCFullYear();
}

/** Extrai "MM-DD" pra indexar aniversariantes (campo denormalizado no Firestore). */
export function extractBirthDayMonthUTC(
  epoch: number | undefined | null,
): string | undefined {
  if (!hasBirthDate(epoch)) return undefined;
  const d = new Date(epoch);
  if (Number.isNaN(d.getTime())) return undefined;
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${mm}-${dd}`;
}
